import axios from 'axios';

import { BACKEND_URL } from '../config';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Attempt to refresh the token via HTTP-only cookie
const refreshToken = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!res.ok) return null;
    
    const data = await res.json();
    // Access token is now set as httpOnly cookie by the server
    // No need to store in localStorage
    return data.token ? true : null;
  } catch (error) {
    console.log('[Auth] Refresh token failed:', error);
    return null;
  }
};

// Call server-side logout to revoke refresh token
export const serverLogout = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (error) {
    console.log('[Auth] Server logout error (non-critical):', error);
  }
};

// Setup axios interceptors for authentication
export const setupAxiosInterceptors = (handleLogout) => {
  // Global: always send cookies (httpOnly access_token + refresh_token)
  axios.defaults.withCredentials = true;

  // Request interceptor — cookies carry both user and admin auth
  axios.interceptors.request.use(
    (config) => {
      // Always send cookies (httpOnly user access_token + refresh_token + admin_token)
      config.withCredentials = true;
      // Note: do NOT read tokens from localStorage anymore. Auth is cookie-only.
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle token expiration with auto-refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Handle HTML responses (server returned error page instead of JSON)
      if (error.response) {
        const contentType = error.response.headers['content-type'];
        
        if (contentType && contentType.includes('text/html')) {
          console.error('[API] Received HTML response instead of JSON:', {
            url: error.config?.url,
            status: error.response.status,
          });
          
          const betterError = new Error('Server returned an unexpected response format');
          betterError.isHTMLResponse = true;
          betterError.originalStatus = error.response.status;
          betterError.config = error.config;
          betterError.response = { 
            data: { 
              error: 'SERVER_ERROR',
              message: 'The server returned an invalid response. Please try again.' 
            },
            status: error.response.status 
          };
          return Promise.reject(betterError);
        }
      } else if (error.request) {
        console.error('[API] No response received:', {
          url: error.config?.url,
        });
      }
      
      // Define endpoints that should NOT trigger automatic logout or refresh on 401
      const AUTH_ENDPOINTS = [
        '/api/auth/register',
        '/api/auth/login',
        '/api/auth/refresh',
        '/api/auth/logout',
        '/api/admin/login',
        '/api/admin/verify',
        '/api/auth/forgot-password',
        '/api/auth/reset-password',
        '/api/auth/verify-token'
      ];
      
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));
      const isAdminApiEndpoint = requestUrl.includes('/admin/');
      
      // Handle 401 Unauthorized errors
      if (error.response && error.response.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
        const isAdminRoute = window.location.pathname.startsWith('/admin');
        
        if (isAdminRoute || isAdminApiEndpoint) {
          const errorDetail = error.response?.data?.detail || '';
          const errorDetailLower = errorDetail.toLowerCase();
          
          const isAdminAuthError = (
            (errorDetailLower.includes('admin') && (errorDetailLower.includes('token') || errorDetailLower.includes('auth'))) ||
            errorDetailLower.includes('not authenticated') ||
            errorDetailLower.includes('could not validate') ||
            errorDetailLower.includes('credentials') ||
            (errorDetailLower.includes('token') && errorDetailLower.includes('expired'))
          );
          
          if (isAdminAuthError) {
            console.log('[Admin Auth] Token invalid or expired, logging out');
            // Best-effort server-side cookie clear
            fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).catch((e) => console.error(e));
            localStorage.removeItem('adminEmail');
            localStorage.removeItem('adminUser');
            if (window.location.pathname !== '/admin/login') {
              window.location.href = '/admin/login';
            }
          }
          return Promise.reject(error);
        }
        
        // For regular users - try to refresh token via cookie
        if (!isRefreshing) {
          isRefreshing = true;
          originalRequest._retry = true;
          
          try {
            const refreshed = await refreshToken();
            
            if (refreshed) {
              console.log('[Auth] Token refreshed successfully via cookie');
              isRefreshing = false;
              onTokenRefreshed(true);
              
              // Retry — cookie is already set by server, no header needed
              return axios(originalRequest);
            } else {
              console.log('[Auth] Refresh failed - logging out');
              isRefreshing = false;
              
              // Clear client-side auth data (cookies are cleared by /auth/logout)
              localStorage.removeItem('user');
              localStorage.removeItem('userId');
              localStorage.removeItem('userEmail');
              localStorage.removeItem('userName');
              
              if (handleLogout) {
                handleLogout();
              }
              
              if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                window.location.href = '/login';
              }
            }
          } catch (refreshError) {
            isRefreshing = false;
            console.log('[Auth] Token refresh error:', refreshError);
            
            localStorage.removeItem('user');
            localStorage.removeItem('userId');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userName');
            
            if (handleLogout) {
              handleLogout();
            }
            
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
              window.location.href = '/login';
            }
          }
        } else {
          return new Promise((resolve) => {
            subscribeTokenRefresh(() => {
              resolve(axios(originalRequest));
            });
          });
        }
      }
      
      return Promise.reject(error);
    }
  );
};
